import React, { useMemo, useState } from 'react';
import { CheckCircle2, XCircle, ListChecks, Play } from 'lucide-react';
import { useChatStore, type PlanPreviewData } from '../../stores/useChatStore.js';

interface PlanPreviewCardProps {
    messageId: string;
    data: PlanPreviewData;
    className?: string;
}

export const PlanPreviewCard: React.FC<PlanPreviewCardProps> = ({
    messageId,
    data,
    className = '',
}) => {
    const approvePlanPreview = useChatStore(s => s.approvePlanPreview);
    const cancelPlanPreview = useChatStore(s => s.cancelPlanPreview);
    const [busy, setBusy] = useState(false);

    const isTerminal = data.status === 'approved' || data.status === 'cancelled';
    const statusLabel = useMemo(() => {
        switch (data.status) {
            case 'approved':
                return 'Approved';
            case 'cancelled':
                return 'Cancelled';
            default:
                return 'Review & approve';
        }
    }, [data.status]);

    const handleApprove = async () => {
        if (busy || isTerminal) return;
        setBusy(true);
        try {
            await approvePlanPreview(messageId);
        } finally {
            setBusy(false);
        }
    };

    const handleCancel = () => {
        if (busy || isTerminal) return;
        cancelPlanPreview(messageId);
    };

    return (
        <div className={`relative overflow-hidden ${className}`}>
            <div className="relative bg-slate-900/60 backdrop-blur-xl border border-slate-700/40 rounded-2xl shadow-xl shadow-black/30">
                <div className="px-5 py-4 border-b border-slate-700/30 flex items-start justify-between gap-4">
                    <div className="flex items-center gap-3 min-w-0">
                        <div className="w-9 h-9 rounded-xl bg-slate-800/50 border border-slate-700/50 flex items-center justify-center flex-shrink-0">
                            {data.status === 'cancelled' ? (
                                <XCircle className="w-5 h-5 text-red-400" />
                            ) : data.status === 'approved' ? (
                                <CheckCircle2 className="w-5 h-5 text-emerald-400" />
                            ) : (
                                <ListChecks className="w-5 h-5 text-cyan-400" />
                            )}
                        </div>
                        <div className="min-w-0">
                            <div className="flex items-center gap-2 flex-wrap">
                                <span className="text-sm font-semibold text-slate-100">
                                    Plan Preview
                                </span>
                                <span className="text-[10px] px-2 py-0.5 rounded-full border border-slate-600/40 bg-slate-800/40 text-slate-300">
                                    {statusLabel}
                                </span>
                                {data.intentName && (
                                    <span className="text-[10px] px-2 py-0.5 rounded-full border border-slate-600/40 bg-slate-800/40 text-slate-300">
                                        Intent: {data.intentName}
                                    </span>
                                )}
                            </div>
                            {data.prompt && (
                                <div className="text-[11px] text-slate-500 mt-1 truncate">
                                    {data.prompt}
                                </div>
                            )}
                        </div>
                    </div>
                </div>

                <div className="px-5 py-4 space-y-4">
                    <div>
                        <div className="text-xs text-slate-500 mb-2">Steps</div>
                        <ul className="space-y-1.5">
                            {data.steps.map((s, idx) => (
                                <li
                                    key={idx}
                                    className="text-xs text-slate-200 flex items-start gap-2"
                                >
                                    <span className="text-slate-500 font-mono">{idx + 1}.</span>
                                    <span className="min-w-0">{s}</span>
                                </li>
                            ))}
                        </ul>
                    </div>

                    <div className="flex items-center gap-2">
                        <button
                            type="button"
                            onClick={handleApprove}
                            disabled={busy || isTerminal}
                            className="inline-flex items-center gap-2 px-3 py-2 rounded-lg bg-emerald-600/20 hover:bg-emerald-600/30 text-emerald-200 text-sm transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                            {busy ? (
                                <div className="w-4 h-4 border-2 border-emerald-200 border-t-transparent rounded-full animate-spin" />
                            ) : (
                                <Play className="w-4 h-4" />
                            )}
                            Approve & Execute
                        </button>
                        <button
                            type="button"
                            onClick={handleCancel}
                            disabled={busy || isTerminal}
                            className="px-3 py-2 rounded-lg bg-slate-700/30 hover:bg-slate-700/45 text-slate-200 text-sm transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                            Cancel
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default PlanPreviewCard;
